import {
  CUIDEI_ATTRIBUTION_COOKIE,
  CUIDEI_ATTRIBUTION_TTL_HOURS,
  CUIDEI_BRAND_COOKIE,
  CUIDEI_EVENTS,
  CUIDEI_PATHOLOGY_COOKIE
} from 'constants/cuidei'
import Cookies from 'js-cookie'
import type { Consultation } from 'types'
import type { CuideiAttribution } from 'types/cuidei'

import { getCookies, setCookies } from './cookieHelpers'

/** Strip everything but digits. */
export const onlyDigits = (value = ''): string => value.replace(/\D/g, '')

// --- Masks (display formatting) ---

export const maskCpf = (value: string): string =>
  onlyDigits(value)
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')

export const maskCep = (value: string): string =>
  onlyDigits(value)
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, '$1-$2')

export const maskPhone = (value: string): string => {
  const digits = onlyDigits(value).slice(0, 11)
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2')
  }
  return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')
}

/** Birth date as DD/MM/AAAA. */
export const maskBirthDate = (value: string): string =>
  onlyDigits(value)
    .slice(0, 8)
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')

/** RG (documento de identidade): digits + optional X, formatted lightly. */
export const maskRg = (value: string): string => value.replace(/[^0-9xX]/g, '').slice(0, 14)

// --- Validators ---

/** Full CPF check-digit validation. */
export const isValidCpf = (value: string): boolean => {
  const cpf = onlyDigits(value)
  if (cpf.length !== 11) return false
  if (/^(\d)\1{10}$/.test(cpf)) return false // all-same-digit CPFs are invalid

  const calcCheckDigit = (slice: string, factorStart: number): number => {
    let sum = 0
    let factor = factorStart
    for (const digit of slice) {
      sum += parseInt(digit, 10) * factor
      factor -= 1
    }
    const remainder = (sum * 10) % 11
    return remainder === 10 ? 0 : remainder
  }

  const firstCheck = calcCheckDigit(cpf.slice(0, 9), 10)
  const secondCheck = calcCheckDigit(cpf.slice(0, 10), 11)
  return firstCheck === parseInt(cpf[9], 10) && secondCheck === parseInt(cpf[10], 10)
}

/** BR mobile/landline: 10 or 11 digits, valid DDD. */
export const isValidPhone = (value: string): boolean => {
  const phone = onlyDigits(value)
  if (phone.length !== 10 && phone.length !== 11) return false
  const ddd = parseInt(phone.slice(0, 2), 10)
  return ddd >= 11 && ddd <= 99
}

/**
 * Strip the +55 country code the backend's phony_normalize stores (E.164).
 * Without this, prefilling "(55) 11940-4063" mangled the number (country code
 * read as DDD, last digit cut by the mask) and re-saving the mangled value
 * failed the backend's phone validation — profile edits never saved
 * (QA 2026-07-09).
 */
export const stripBrCountryCode = (value: string): string => {
  const digits = onlyDigits(value)
  if (digits.length >= 12 && digits.startsWith('55')) return digits.slice(2)
  return digits
}

/** CEP format only (8 digits). Resolution is validated separately via lookup. */
export const isValidCep = (value: string): boolean => onlyDigits(value).length === 8

/** RG present with a reasonable length (no national checksum exists). */
export const isValidRg = (value: string): boolean => value.replace(/[^0-9xX]/g, '').length >= 5

/** RNM / passaporte: alphanumeric, uppercase, 5–12 chars (no national checksum). */
export const maskIdentityDoc = (value: string): string =>
  value
    .replace(/[^0-9a-zA-Z]/g, '')
    .toUpperCase()
    .slice(0, 12)
/** rg_number as the backend stores it: RNM / passport ride prefixed (free-form varchar). */
export const formatCuideiIdentityDocument = (
  docType: 'rg' | 'rnm' | 'passaporte' | undefined,
  value: string
): string => (docType === 'rnm' ? `RNM: ${value}` : docType === 'passaporte' ? `Passaporte: ${value}` : value)

export const isValidIdentityDoc = (value: string): boolean => value.replace(/[^0-9a-zA-Z]/g, '').length >= 5

export type CuideiIdentityDocumentType = 'rg' | 'rnm' | 'passaporte'

export const parseCuideiIdentityDocument = (storedValue = ''): { type: CuideiIdentityDocumentType; value: string } => {
  const rnm = storedValue.match(/^RNM:\s*(.*)$/i)
  if (rnm) return { type: 'rnm', value: maskIdentityDoc(rnm[1]) }

  const passport = storedValue.match(/^Passaporte:\s*(.*)$/i)
  if (passport) return { type: 'passaporte', value: maskIdentityDoc(passport[1]) }

  return { type: 'rg', value: maskRg(storedValue) }
}

export const serializeCuideiIdentityDocument = (type: CuideiIdentityDocumentType, value: string): string => {
  if (type === 'rnm') return `RNM: ${value}`
  if (type === 'passaporte') return `Passaporte: ${value}`
  return value
}

/** DD/MM/AAAA birth date: real calendar date, plausible year, not in the future. */
export const isValidBirthDate = (value: string): boolean => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim())
  if (!match) return false
  const day = parseInt(match[1], 10)
  const month = parseInt(match[2], 10)
  const year = parseInt(match[3], 10)
  if (month < 1 || month > 12 || day < 1 || day > 31) return false
  const date = new Date(year, month - 1, day)
  const isRealDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
  return isRealDate && year >= 1900 && date.getTime() <= Date.now()
}

// --- Pharmacy attribution ---

/** Generate a reasonably-unique client session id without an extra dependency. */
const generateSessionId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `cuidei-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

/**
 * Capture pharmacy attribution from the current URL (QR-code entry).
 *
 * Reads pharmacy_id (or an explicit override from /p/[pharmacyId]) + UTMs,
 * generates a session id, persists everything to a cookie, and marks the
 * session as a Cuidei brand session. First capture wins — re-landing without
 * the param keeps the original attribution (spec §5: attribution must persist).
 */
export const captureCuideiAttribution = (overridePharmacyId?: string): CuideiAttribution => {
  const existing = getStoredCuideiAttribution()

  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
  const incomingPharmacyId = overridePharmacyId ?? params.get('pharmacy_id')
  const pharmacyId = existing?.pharmacyId ?? incomingPharmacyId ?? null

  // Keep the first real pharmacy. The only allowed upgrade is null -> a QR
  // pharmacy; subsequent scans must not change routing or commission.
  if (existing && (!incomingPharmacyId || existing.pharmacyId)) {
    markCuideiBrandSession()
    return existing
  }

  const attribution: CuideiAttribution = {
    pharmacyId,
    utmSource: params.get('utm_source') ?? existing?.utmSource ?? null,
    utmMedium: params.get('utm_medium') ?? existing?.utmMedium ?? null,
    utmCampaign: params.get('utm_campaign') ?? existing?.utmCampaign ?? null,
    landingAt: existing?.landingAt ?? new Date().toISOString(),
    sessionId: existing?.sessionId ?? generateSessionId()
  }

  setCookies({
    name: CUIDEI_ATTRIBUTION_COOKIE,
    value: JSON.stringify(attribution),
    expirationHours: CUIDEI_ATTRIBUTION_TTL_HOURS
  })
  markCuideiBrandSession()

  return attribution
}

export const getStoredCuideiAttribution = (): CuideiAttribution | null => {
  const { value } = getCookies(CUIDEI_ATTRIBUTION_COOKIE)
  if (!value) return null
  try {
    return JSON.parse(value) as CuideiAttribution
  } catch {
    return null
  }
}

/** Mark the session as Cuidei so the patient area / layout pick the brand. */
export const markCuideiBrandSession = (): void => {
  setCookies({ name: CUIDEI_BRAND_COOKIE, value: 'true', expirationHours: CUIDEI_ATTRIBUTION_TTL_HOURS })
}

export const isCuideiConsultation = (consultation: Consultation): boolean =>
  Boolean(consultation.isCuidei || consultation.consultationType === 'cuidei' || consultation.speciality === 'cuidei')

// --- Tracking (spec §27) ---

/**
 * Emit a Cuidei tracking event to GTM dataLayer (and any future backend sink).
 * Enriches with attribution so every event carries pharmacy + session context.
 */
/**
 * Remember which pathology landing the current flow belongs to (null = the
 * pharmacy /farma flow). A cookie, not module state, so patient-area events
 * after a reload are still attributed to the pathology.
 */
export const rememberCuideiPathology = (slug: string | null): void => {
  if (slug) {
    setCookies({ name: CUIDEI_PATHOLOGY_COOKIE, value: slug, expirationHours: CUIDEI_ATTRIBUTION_TTL_HOURS })
  } else {
    Cookies.remove(CUIDEI_PATHOLOGY_COOKIE)
  }
}

export const trackCuideiEvent = (eventName: string, metadata: Record<string, unknown> = {}): void => {
  if (typeof window === 'undefined') return
  const attribution = getStoredCuideiAttribution()
  const payload = {
    event: `cuidei_${eventName}`,
    pharmacy_id: attribution?.pharmacyId ?? null,
    session_id: attribution?.sessionId ?? null,
    utm_source: attribution?.utmSource ?? null,
    utm_medium: attribution?.utmMedium ?? null,
    utm_campaign: attribution?.utmCampaign ?? null,
    pathology_slug: getCookies(CUIDEI_PATHOLOGY_COOKIE).value ?? null,
    ...metadata
  }
  // @ts-ignore — GTM dataLayer is injected by the GTM script.
  window.dataLayer = window.dataLayer || []
  // @ts-ignore
  window.dataLayer.push(payload)
}

// Flows whose form start was already sent from this page load ('' = /farma).
const startedForms = new Set<string>()

/**
 * cuidei_patient_form_started, ONCE per page load and flow, with the flow's own slug (null on /farma):
 * going back to the first step or a re-render must not count a second start.
 */
export const trackCuideiFormStarted = (pathologySlug: string | null): void => {
  const key = pathologySlug ?? ''
  if (startedForms.has(key)) return
  startedForms.add(key)
  trackCuideiEvent(CUIDEI_EVENTS.patientFormStarted, { pathology_slug: pathologySlug })
}

const PURCHASES_STORAGE_KEY = 'cuideiTrackedPurchases'
// Fallback when localStorage is unavailable (private mode, blocked storage).
const trackedPurchases = new Set<string>()

const readTrackedPurchases = (): string[] => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(PURCHASES_STORAGE_KEY) ?? '[]')
    return Array.isArray(stored) ? stored : []
  } catch {
    return []
  }
}

/** Google Ads enhanced conversions: e-mail lowercased, without any whitespace. */
export const normalizeAdsEmail = (value = ''): string | undefined => {
  const email = value.replace(/\s+/g, '').toLowerCase()
  return /^[^@]+@[^@]+\.[^@]+$/.test(email) ? email : undefined
}

/** Google Ads enhanced conversions: a Brazilian phone (masked, national or +55) in E.164. */
export const toBrE164Phone = (value = ''): string | undefined => {
  const national = stripBrCountryCode(value)
  return isValidPhone(national) ? `+55${national}` : undefined
}

/**
 * cuidei_purchase (the GA4 / Google Ads conversion in the Cuidei GTM), sent only from the payment
 * success callbacks and at most ONCE per order, also across reloads of the page. `user_data` feeds
 * the Ads enhanced conversions and carries ONLY the e-mail and phone: never health data (answers,
 * symptoms, diagnosis) in the dataLayer.
 */
export const trackCuideiPurchase = ({
  transactionId,
  value,
  pathologySlug,
  contact
}: {
  transactionId: string
  value: number
  pathologySlug: string
  contact?: { email?: string | null; phone?: string | null }
}): void => {
  if (typeof window === 'undefined') return
  const stored = readTrackedPurchases()
  if (trackedPurchases.has(transactionId) || stored.includes(transactionId)) return
  trackedPurchases.add(transactionId)
  try {
    window.localStorage.setItem(PURCHASES_STORAGE_KEY, JSON.stringify([...stored, transactionId].slice(-20)))
  } catch {
    // The in-memory set still covers this page load.
  }
  const email = normalizeAdsEmail(contact?.email ?? '')
  const phoneNumber = toBrE164Phone(contact?.phone ?? '')
  trackCuideiEvent(CUIDEI_EVENTS.purchase, {
    transaction_id: transactionId,
    value,
    currency: 'BRL',
    pathology_slug: pathologySlug,
    ...((email || phoneNumber) && {
      user_data: { ...(email && { email }), ...(phoneNumber && { phone_number: phoneNumber }) }
    })
  })
}
